import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Copy, Ban, Link2, RefreshCw, Loader2, MessageCircle } from "lucide-react";

// Director-only page: issue / list / revoke magic-link tokens for vendors.
// Public URL format: {origin}/portal/vendor?t=<raw>
export default function PortalTokens() {
  const { lang } = useLang();
  const t = (en, hi) => (lang === "hi" ? hi : en);
  const [vendors, setVendors] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [vendorId, setVendorId] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null); // { token, vendor_name, expires_at }
  const [msg, setMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [v, tk] = await Promise.all([
        api.get("/vendors"),
        api.get("/portal/tokens"),
      ]);
      setVendors(v.data || []);
      setTokens(tk.data || []);
    } catch (e) {
      setMsg(e?.response?.data?.detail || e.message);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const issue = async () => {
    if (!vendorId) { setMsg(t("Select a vendor first", "पहले वेंडर चुनें")); return; }
    setBusy(true); setMsg("");
    try {
      const r = await api.post("/portal/tokens", { vendor_id: vendorId });
      setIssued(r.data);
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const revoke = async (id) => {
    if (!window.confirm(t("Revoke this link?", "इस लिंक को रद्द करें?"))) return;
    try {
      await api.post(`/portal/tokens/${id}/revoke`);
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || e.message); }
  };

  const buildLink = (raw) => `${window.location.origin}/portal/vendor?t=${encodeURIComponent(raw)}`;

  const buildWhatsAppMessage = (vendorName, url) => {
    const en = `Namaste ${vendorName}, this is your HAG portal link to update the status of your open lines. No login needed — just tap and update.\n\n${url}\n\n— HAG Carpets, Mirzapur`;
    const hi = `नमस्ते ${vendorName}, यह HAG का पोर्टल लिंक है — अपनी लंबित लाइनों की स्थिति अपडेट करने के लिए। कोई लॉगिन नहीं, बस लिंक खोलें।\n\n${url}\n\n— HAG कारपेट्स, मिर्ज़ापुर`;
    return `${en}\n\n${hi}`;
  };

  const shareWhatsApp = (vendorName, raw) => {
    const url = buildLink(raw);
    const msg = buildWhatsAppMessage(vendorName, url);
    // wa.me works on both mobile and desktop web WhatsApp; no phone → open chat picker
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); setMsg(t("Copied to clipboard", "कॉपी हो गया")); }
    catch { setMsg(t("Copy failed — long-press to copy", "कॉपी नहीं हुआ — देर तक दबाकर कॉपी करें")); }
  };

  const vendorName = (id) => vendors.find((v) => v.id === id)?.name || id;

  return (
    <div data-testid="portal-tokens-page">
      <h1 className="text-2xl font-bold mb-1" style={{ color: "var(--brand-navy)", fontFamily: "'Fraunces', serif" }}>
        {t("Vendor Portal Links", "वेंडर पोर्टल लिंक")}
      </h1>
      <p className="text-xs text-[#7C8E9E] mb-4">
        {t("Issue a magic-link so a vendor can update line status without any login. Links expire in 90 days.",
           "मैजिक-लिंक जारी करें — वेंडर बिना लॉगिन के लाइन स्थिति अपडेट करेगा। 90 दिन तक मान्य।")}
      </p>

      {msg && <div className="mb-3 text-xs bg-amber-50 border border-amber-200 rounded p-2" data-testid="portal-tokens-msg">{msg}</div>}

      <div className="hag-surface p-4 mb-6">
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[240px]">
            <label className="block text-[10px] uppercase tracking-widest font-bold text-[#4A627A] mb-1">{t("Vendor", "वेंडर")}</label>
            <select data-testid="portal-token-vendor-select" value={vendorId}
              onChange={(e) => setVendorId(e.target.value)}
              className="w-full h-11 px-2 border border-[#1B3A5C]/15 rounded-md text-sm bg-white">
              <option value="">— {t("select", "चुनें")} —</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <button data-testid="portal-token-issue-btn" onClick={issue} disabled={busy || !vendorId}
            className="h-11 px-4 rounded-md text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
            style={{ background: "var(--brand-navy)", color: "white" }}>
            {busy && <Loader2 size={14} className="animate-spin" />} <Link2 size={14} /> {t("Issue link", "लिंक जारी करें")}
          </button>
          <button onClick={load} className="h-11 px-3 rounded-md border border-[#1B3A5C]/20 text-sm flex items-center gap-1" data-testid="portal-token-refresh">
            <RefreshCw size={14} /> {t("Refresh", "रीफ्रेश")}
          </button>
        </div>

        {issued && (
          <div className="mt-4 p-3 border border-[#C8A86B] rounded-md bg-[#FAF6F0]" data-testid="portal-token-issued-box">
            <div className="text-xs text-[#4A627A] font-semibold mb-1">{t("New link for", "नया लिंक")}: {issued.vendor_name}</div>
            <div className="flex items-center gap-2 flex-wrap">
              <code className="text-xs bg-white border border-[#1B3A5C]/20 rounded px-2 py-1 flex-1 break-all" data-testid="portal-token-issued-link">
                {buildLink(issued.token)}
              </code>
              <button onClick={() => copy(buildLink(issued.token))}
                className="h-9 px-3 rounded-md text-xs font-semibold flex items-center gap-1"
                style={{ background: "var(--brand-gold)", color: "var(--brand-navy)" }}
                data-testid="portal-token-copy-btn">
                <Copy size={12} /> {t("Copy", "कॉपी")}
              </button>
              <button onClick={() => shareWhatsApp(issued.vendor_name, issued.token)}
                className="h-9 px-3 rounded-md text-xs font-semibold flex items-center gap-1 text-white"
                style={{ background: "#25D366" }}
                data-testid="portal-token-whatsapp-btn">
                <MessageCircle size={12} /> {t("Send on WhatsApp", "WhatsApp पर भेजें")}
              </button>
            </div>
            <div className="text-[10px] text-[#7C8E9E] mt-2">
              {t("Share with vendor via WhatsApp. Token is shown once — after this page navigates away, only its short id remains in the list below.",
                 "WhatsApp पर वेंडर को भेजें। टोकन केवल एक बार दिखता है — पेज बदलने के बाद केवल आईडी सूची में रहती है।")}
            </div>
          </div>
        )}
      </div>

      <h2 className="text-sm font-bold uppercase tracking-widest text-[#4A627A] mb-2">
        {t("Active & recent links", "सक्रिय व हाल के लिंक")}
      </h2>
      {loading ? <div className="text-sm text-[#7C8E9E]">Loading…</div> : (
        <div className="hag-surface overflow-x-auto" data-testid="portal-tokens-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[#7C8E9E] bg-[#FAF6F0]">
                <th className="text-left px-3 py-2">{t("Vendor", "वेंडर")}</th>
                <th className="text-left px-3 py-2">{t("Issued", "जारी")}</th>
                <th className="text-left px-3 py-2">{t("Expires", "समाप्ति")}</th>
                <th className="text-right px-3 py-2">{t("Uses", "उपयोग")}</th>
                <th className="text-right px-3 py-2">{t("Updates", "अपडेट")}</th>
                <th className="text-left px-3 py-2">{t("Status", "स्थिति")}</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((r) => (
                <tr key={r.id} className="border-t border-[#1B3A5C]/10" data-testid={`portal-token-row-${r.id}`}>
                  <td className="px-3 py-2 font-semibold">{r.vendor_name || vendorName(r.vendor_id)}</td>
                  <td className="px-3 py-2 text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td className="px-3 py-2 text-xs">{new Date(r.expires_at).toLocaleDateString()}</td>
                  <td className="px-3 py-2 text-right text-xs">{r.use_count || 0}</td>
                  <td className="px-3 py-2 text-right text-xs">{r.updates_count || 0}</td>
                  <td className="px-3 py-2">
                    {r.revoked ? <span className="status-chip pace-red">{t("Revoked", "रद्द")}</span>
                      : new Date(r.expires_at) < new Date() ? <span className="status-chip pace-amber">{t("Expired", "समाप्त")}</span>
                      : <span className="status-chip pace-green">{t("Active", "सक्रिय")}</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {!r.revoked && (
                      <button onClick={() => revoke(r.id)} className="text-xs text-red-700 hover:underline flex items-center gap-1 ml-auto"
                        data-testid={`portal-token-revoke-${r.id}`}>
                        <Ban size={12} /> {t("Revoke", "रद्द करें")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {tokens.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-xs text-[#7C8E9E]">{t("No links issued yet.", "अभी कोई लिंक नहीं।")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
